import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type {
  Resolution,
  ResolutionInsert,
  ResolutionUpdate,
  ResolutionFilters,
  VoteResult,
  OwnerVoteInsert,
  ResolutionTemplate,
  ResolutionCategory,
} from '../types'
import { meetingKeys } from './useMeetings'

// ===== QUERY KEYS =====
export const resolutionKeys = {
  all: ['resolutions'] as const,
  lists: () => [...resolutionKeys.all, 'list'] as const,
  list: (filters: ResolutionFilters) => [...resolutionKeys.lists(), filters] as const,
  details: () => [...resolutionKeys.all, 'detail'] as const,
  detail: (id: string) => [...resolutionKeys.details(), id] as const,
  votes: (id: string) => [...resolutionKeys.all, 'votes', id] as const,
  result: (id: string) => [...resolutionKeys.all, 'result', id] as const,
  templates: () => [...resolutionKeys.all, 'templates'] as const,
  templatesByCategory: (category: ResolutionCategory) => [...resolutionKeys.templates(), category] as const,
}

// ===== HOOKS =====

/**
 * Liste des résolutions d'une AG
 */
export function useResolutions(meetingId: string | undefined) {
  return useQuery({
    queryKey: resolutionKeys.list({ meeting_id: meetingId }),
    queryFn: async (): Promise<Resolution[]> => {
      const { data, error } = await supabase
        .from('resolutions')
        .select(`
          *,
          template:resolution_templates(id, code, title),
          distribution_key:distribution_keys(id, name),
          budget:budgets(id, name),
          supplier:suppliers(id, name, code)
        `)
        .eq('meeting_id', meetingId!)
        .order('order_number', { ascending: true })

      if (error) throw error
      return data as Resolution[]
    },
    enabled: !!meetingId,
  })
}

/**
 * Détail d'une résolution avec votes
 */
export function useResolution(resolutionId: string | undefined) {
  return useQuery({
    queryKey: resolutionKeys.detail(resolutionId!),
    queryFn: async (): Promise<Resolution> => {
      const { data, error } = await supabase
        .from('resolutions')
        .select(`
          *,
          meeting:meetings(id, title, meeting_date),
          template:resolution_templates(id, code, title),
          distribution_key:distribution_keys(id, name),
          budget:budgets(id, name),
          supplier:suppliers(id, name, code),
          annexes:resolution_annexes(
            *,
            document:documents(id, display_name, original_filename)
          ),
          votes:owner_votes(
            *,
            owner:owners!owner_votes_owner_id_fkey(id, first_name, last_name)
          )
        `)
        .eq('id', resolutionId!)
        .single()

      if (error) throw error
      return data as Resolution
    },
    enabled: !!resolutionId,
  })
}

/**
 * Créer une résolution
 */
export function useCreateResolution() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: ResolutionInsert): Promise<Resolution> => {
      // Récupérer le prochain order_number
      const { data: lastResolution } = await supabase
        .from('resolutions')
        .select('order_number')
        .eq('meeting_id', data.meeting_id)
        .order('order_number', { ascending: false })
        .limit(1)
        .single()

      const nextOrderNumber = (lastResolution?.order_number || 0) + 1

      const { data: resolution, error } = await supabase
        .from('resolutions')
        .insert({
          ...data,
          order_number: nextOrderNumber,
        })
        .select(`
          *,
          template:resolution_templates(id, code, title),
          distribution_key:distribution_keys(id, name),
          budget:budgets(id, name),
          supplier:suppliers(id, name, code)
        `)
        .single()

      if (error) throw error
      return resolution as Resolution
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: resolutionKeys.list({ meeting_id: data.meeting_id }),
      })
      queryClient.invalidateQueries({
        queryKey: meetingKeys.detail(data.meeting_id),
      })
    },
  })
}

/**
 * Mettre à jour une résolution
 */
export function useUpdateResolution() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: ResolutionUpdate
    }): Promise<Resolution> => {
      const { data: resolution, error } = await supabase
        .from('resolutions')
        .update(data)
        .eq('id', id)
        .select(`
          *,
          template:resolution_templates(id, code, title),
          distribution_key:distribution_keys(id, name),
          budget:budgets(id, name),
          supplier:suppliers(id, name, code)
        `)
        .single()

      if (error) throw error
      return resolution as Resolution
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: resolutionKeys.list({ meeting_id: data.meeting_id }),
      })
      queryClient.setQueryData(resolutionKeys.detail(data.id), data)
      queryClient.invalidateQueries({
        queryKey: meetingKeys.detail(data.meeting_id),
      })
    },
  })
}

/**
 * Supprimer une résolution
 */
export function useDeleteResolution() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
    }: {
      id: string
      meetingId: string
    }): Promise<void> => {
      const { error } = await supabase.from('resolutions').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: (_, { meetingId }) => {
      queryClient.invalidateQueries({
        queryKey: resolutionKeys.list({ meeting_id: meetingId }),
      })
      queryClient.invalidateQueries({
        queryKey: meetingKeys.detail(meetingId),
      })
    },
  })
}

/**
 * Réordonner les résolutions (drag & drop)
 */
export function useReorderResolutions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      orderedIds,
    }: {
      meetingId: string
      orderedIds: string[]
    }): Promise<void> => {
      // Mettre à jour l'ordre de chaque résolution
      const updates = orderedIds.map((id, index) =>
        supabase
          .from('resolutions')
          .update({ order_number: index + 1 })
          .eq('id', id)
      )

      await Promise.all(updates)
    },
    onSuccess: (_, { meetingId }) => {
      queryClient.invalidateQueries({
        queryKey: resolutionKeys.list({ meeting_id: meetingId }),
      })
    },
  })
}

/**
 * Enregistrer un vote sur une résolution
 */
export function useRecordVote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (vote: OwnerVoteInsert): Promise<void> => {
      const { error } = await supabase.from('owner_votes').upsert(vote, {
        onConflict: 'owner_id,resolution_id',
      })
      if (error) throw error
    },
    onSuccess: (_, vote) => {
      queryClient.invalidateQueries({
        queryKey: resolutionKeys.votes(vote.resolution_id),
      })
      queryClient.invalidateQueries({
        queryKey: resolutionKeys.result(vote.resolution_id),
      })
    },
  })
}

/**
 * Calculer le résultat d'un vote
 */
export function useResolutionResult(resolutionId: string | undefined) {
  return useQuery({
    queryKey: resolutionKeys.result(resolutionId!),
    queryFn: async (): Promise<VoteResult> => {
      // Récupérer la résolution avec son type de majorité
      const { data: resolution, error: resError } = await supabase
        .from('resolutions')
        .select('majority_type, threshold_shares, meeting_id')
        .eq('id', resolutionId!)
        .single()

      if (resError) throw resError

      // Récupérer le total des tantièmes de l'AG
      const { data: meeting, error: meetError } = await supabase
        .from('meetings')
        .select('total_shares_convoked, total_shares_present')
        .eq('id', resolution.meeting_id)
        .single()

      if (meetError) throw meetError

      // Récupérer les votes avec les tantièmes
      const { data: votes, error: votesError } = await supabase
        .from('owner_votes')
        .select('vote_type, shares_voted')
        .eq('resolution_id', resolutionId!)

      if (votesError) throw votesError

      let sharesFor = 0
      let sharesAgainst = 0
      let sharesAbstain = 0

      for (const vote of votes || []) {
        const shares = vote.shares_voted || 0
        switch (vote.vote_type) {
          case 'for':
            sharesFor += shares
            break
          case 'against':
            sharesAgainst += shares
            break
          case 'abstain':
            sharesAbstain += shares
            break
        }
      }

      const totalShares = meeting?.total_shares_convoked || 0

      // Calculer si la majorité est atteinte selon le type
      let isAdopted = false
      let majorityReached = false
      let secondVotePossible = false

      switch (resolution.majority_type) {
        case 'simple':
          // Majorité des voix exprimées
          isAdopted = sharesFor > sharesAgainst
          majorityReached = isAdopted
          break

        case 'absolute':
          // Majorité absolue (> 50% de tous les tantièmes)
          isAdopted = sharesFor > totalShares / 2
          majorityReached = isAdopted
          // Art. 25-1: si >= 1/3 des voix, second vote possible
          if (!isAdopted && sharesFor >= totalShares / 3) {
            secondVotePossible = true
          }
          break

        case 'absolute_25_1':
          // Second vote après passerelle 25-1 (majorité simple)
          isAdopted = sharesFor > sharesAgainst
          majorityReached = isAdopted
          break

        case 'double':
          // Double majorité: majorité des membres + 2/3 des voix
          // Simplifié: > 2/3 des voix
          isAdopted = sharesFor > (totalShares * 2) / 3
          majorityReached = isAdopted
          break

        case 'unanimity':
          // Unanimité de tous les tantièmes
          isAdopted = sharesFor === totalShares && sharesAgainst === 0
          majorityReached = isAdopted
          break
      }

      return {
        resolution_id: resolutionId!,
        total_shares: totalShares,
        shares_for: sharesFor,
        shares_against: sharesAgainst,
        shares_abstain: sharesAbstain,
        is_adopted: isAdopted,
        majority_reached: majorityReached,
        second_vote_possible: secondVotePossible,
      }
    },
    enabled: !!resolutionId,
  })
}

/**
 * Liste des modèles de résolutions (templates)
 */
export function useResolutionTemplates(agencyId?: string) {
  return useQuery({
    queryKey: resolutionKeys.templates(),
    queryFn: async (): Promise<ResolutionTemplate[]> => {
      let query = supabase
        .from('resolution_templates')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })

      // Récupérer les templates globaux (agency_id IS NULL) et ceux de l'agence
      if (agencyId) {
        query = query.or(`agency_id.is.null,agency_id.eq.${agencyId}`)
      } else {
        query = query.is('agency_id', null)
      }

      const { data, error } = await query

      if (error) throw error
      return data as ResolutionTemplate[]
    },
  })
}

/**
 * Modèles de résolutions par catégorie
 */
export function useResolutionTemplatesByCategory(
  category: ResolutionCategory,
  agencyId?: string
) {
  return useQuery({
    queryKey: resolutionKeys.templatesByCategory(category),
    queryFn: async (): Promise<ResolutionTemplate[]> => {
      let query = supabase
        .from('resolution_templates')
        .select('*')
        .eq('is_active', true)
        .eq('resolution_category', category)
        .order('sort_order', { ascending: true })

      if (agencyId) {
        query = query.or(`agency_id.is.null,agency_id.eq.${agencyId}`)
      } else {
        query = query.is('agency_id', null)
      }

      const { data, error } = await query

      if (error) throw error
      return data as ResolutionTemplate[]
    },
    enabled: !!category,
  })
}
